import { IconButton, Icons, Tooltip } from '@commenda/kakashi';
import { classNames } from '@commenda/utils';
import { ReactNode, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StackedDataPointProps {
  /** Field label — displayed above the value. */
  label: ReactNode;

  /** Field value — can be a string, ReactNode (e.g. Badge), or null. */
  value: ReactNode;

  /** Optional tooltip shown via an info icon next to the label. */
  description?: string;

  /**
   * Field type.
   * - `'text'` (default): renders value as-is.
   * - `'password'`: masks the value and shows a show/hide toggle.
   */
  type?: 'text' | 'password';

  /** When provided a copy button is shown and called on click. */
  onCopy?: () => void;

  /** Additional class names applied to the outer wrapper. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * A stacked (label-on-top, value-below) data display component.
 *
 * Supports:
 * - Copy-to-clipboard button
 * - Password masking with show/hide toggle
 * - Tooltip descriptions on the label
 * - ReactNode values (e.g. Badge, custom markup)
 *
 * Designed as a reusable building block for detail panels, side sheets, etc.
 */
export function StackedDataPoint({
  label,
  value,
  description,
  type = 'text',
  onCopy,
  className,
}: StackedDataPointProps) {
  const [passwordVisible, setPasswordVisible] = useState(false);

  const isPassword = type === 'password';

  const displayValue = () => {
    if (value == null || value === '') return '-';

    if (isPassword && !passwordVisible) {
      return typeof value === 'string'
        ? '•'.repeat(Math.min(16, value.length))
        : '••••••••';
    }

    return value;
  };

  const hasActions = !!onCopy || (isPassword && value);

  return (
    <div
      className={classNames(
        'flex items-start justify-between gap-4 py-3 px-6',
        className
      )}
    >
      {/* Label + Value */}
      <div className="flex flex-col gap-1 min-w-0">
        <div className="txt-extra-small-medium text-text-secondary flex items-center gap-1">
          {label}
          {description && (
            <Tooltip content={description} side="top">
              <Icons.InfoCircle className="size-3.5 text-text-quaternary hover:text-fg-quaternary-hover shrink-0" />
            </Tooltip>
          )}
        </div>
        <div className="txt-small-medium text-text-primary break-words">
          {displayValue()}
        </div>
      </div>

      {/* Action buttons */}
      {hasActions && (
        <div className="flex items-center gap-1 shrink-0 mt-0.5">
          {isPassword && value && (
            <Tooltip content={passwordVisible ? 'Hide' : 'Show'} side="bottom">
              <IconButton
                icon={
                  passwordVisible ? (
                    <Icons.EyeOff className="text-text-quaternary group-hover:text-fg-brand" />
                  ) : (
                    <Icons.Eye className="text-text-quaternary group-hover:text-fg-brand" />
                  )
                }
                size="sm"
                aria-label={passwordVisible ? 'Hide value' : 'Show value'}
                onClick={() => setPasswordVisible((v) => !v)}
              />
            </Tooltip>
          )}
          {onCopy && (
            <Tooltip content="Copy" side="bottom">
              <IconButton
                icon={
                  <Icons.Copy06 className="text-text-quaternary group-hover:text-fg-brand" />
                }
                size="sm"
                aria-label="Copy to clipboard"
                onClick={onCopy}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}
